'use client';

// Reusable pagination component for search results
// Uses LinkWrapper for navigation links so global link semantics are consistent

import { JSX, MouseEvent, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { tv } from 'tailwind-variants';
import { getTestProps } from 'lib/testing/utils';
import useDictionary from 'lib/hooks/useDictionary';
import { RemixIcon } from 'helpers/RemixIcon';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';

export type SearchPaginationProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  showPageNumbers?: boolean;
  maxVisiblePages?: number;
};

const SearchPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  showPageNumbers = true,
  maxVisiblePages = 5,
}: SearchPaginationProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();

  const buildPageLinkField = useCallback(
    (page: number) => {
      const sp = new URLSearchParams(searchParams.toString());
      if (page > 1) {
        sp.set('page', String(page));
      } else {
        sp.delete('page');
      }
      const qs = sp.toString();
      const href = qs ? `${pathname}?${qs}` : pathname;

      return {
        value: { href, linktype: 'internal' as const },
      };
    },
    [pathname, searchParams]
  );

  // Calculate visible page numbers
  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    const end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();
  //   const startItem = (currentPage - 1) * itemsPerPage + 1;
  //   const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const { base, navigationWrapper, pageButton, pagePrevNextButton, ellipsis } = TAILWIND_VARIANTS();

  if (totalPages <= 1) {
    return <></>;
  }

  return (
    <div className={base()} {...getTestProps('search-pagination')}>
      {/* <div className={infoText()}>
        {getDictionaryValue('Showing') || 'Showing'} {startItem}-{endItem} {getDictionaryValue('of') || 'of'} {totalItems} {getDictionaryValue('results') || 'results'}
      </div> */}

      <div className={navigationWrapper()}>
        {/* Previous Link */}
        {currentPage > 1 && (
          <LinkWrapper
            ctaVariant="custom"
            editable={false}
            field={buildPageLinkField(currentPage - 1)}
            className={pagePrevNextButton()}
            onClick={handlePrevious}
            aria-label={getDictionaryValue('Previous') || 'Previous page'}
            suppressNewTabIcon
            {...getTestProps('pagination-prev')}
          >
            <RemixIcon name="ri-arrow-left-s-line" size="md" />
          </LinkWrapper>
        )}

        {/* Page Numbers */}
        {showPageNumbers && (
          <>
            {/* First page if not visible */}
            {visiblePages[0] > 1 && (
              <>
                <LinkWrapper
                  ctaVariant="custom"
                  editable={false}
                  field={buildPageLinkField(1)}
                  className={pageButton({ isActive: false })}
                  onClick={handlePageClick(1)}
                  suppressNewTabIcon
                  {...getTestProps('pagination-page-1')}
                >
                  1
                </LinkWrapper>
                {visiblePages[0] > 2 && <span className={ellipsis()}>...</span>}
              </>
            )}

            {/* Visible page numbers */}
            {visiblePages.map((page) => (
              <LinkWrapper
                key={page}
                ctaVariant="custom"
                editable={false}
                field={buildPageLinkField(page)}
                className={pageButton({ isActive: page === currentPage })}
                onClick={handlePageClick(page)}
                aria-current={page === currentPage ? 'page' : undefined}
                suppressNewTabIcon
                {...getTestProps(`pagination-page-${page}`)}
              >
                {page}
              </LinkWrapper>
            ))}

            {/* Last page if not visible */}
            {visiblePages[visiblePages.length - 1] < totalPages && (
              <>
                {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                  <span className={ellipsis()}>...</span>
                )}
                <LinkWrapper
                  ctaVariant="custom"
                  editable={false}
                  field={buildPageLinkField(totalPages)}
                  className={pageButton({ isActive: false })}
                  onClick={handlePageClick(totalPages)}
                  suppressNewTabIcon
                  {...getTestProps(`pagination-page-${totalPages}`)}
                >
                  {totalPages}
                </LinkWrapper>
              </>
            )}
          </>
        )}

        {/* Next Link */}
        {currentPage < totalPages && (
          <LinkWrapper
            ctaVariant="custom"
            editable={false}
            field={buildPageLinkField(currentPage + 1)}
            className={pagePrevNextButton()}
            onClick={handleNext}
            aria-label={getDictionaryValue('Next') || 'Next page'}
            suppressNewTabIcon
            {...getTestProps('pagination-next')}
          >
            <RemixIcon name="ri-arrow-right-s-line" size="md" />
          </LinkWrapper>
        )}
      </div>
    </div>
  );
};

export default SearchPagination;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['w-full', 'flex', 'flex-row', 'justify-between', 'items-center'],
    navigationWrapper: [
      'w-full',
      'h-full',
      'flex',
      'flex-row',
      'items-center',
      'justify-start',
      'gap-spacing-spacing-8',
    ],
    pageButton: [
      'flex',
      'items-center',
      'justify-center',
      'w-11',
      'h-6',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'text-center',
      'font-bold',
      'rounded-general-border-radius-tag',
    ],
    pagePrevNextButton: [
      'flex',
      'items-center',
      'justify-center',
      'w-6',
      'h-6',
      'p-spacing-spacing-8',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'text-color-fill-dark',
      'text-center',
      'font-bold',
    ],
    ellipsis: [
      'flex',
      'items-center',
      'justify-center',
      'w-8',
      'h-8',
      'text-color-text-secondary',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'font-semibold',
    ],
  },
  variants: {
    isActive: {
      true: {
        pageButton: ['bg-color-core-color-4-SkyBlue', 'text-color-icon-dark'],
      },
      false: {
        pageButton: [
          'bg-color-grayscale-white',
          'text-color-text-text-secondary',
          'hover:bg-color-core-color-4-skyblue-lightTint',
          'hover:text-color-icon-dark',
        ],
      },
    },
  },
});
